import { Vector3 } from "@babylonjs/core/Maths/math.vector";
export class BufferedRigidBodyBundleImpl {
    shouldSync;
    _isDirty;
    /**
     * for setTransformMatrix
     */
    _motionStateMatricesBuffer;
    _isMotionStateMatricesBufferDirty;
    _motionStateMatrixDirtyFlags;
    /**
     * for setDynamicTransformMatrix
     */
    _dynamicTransformMatricesBuffer;
    _isDynamicTransformMatricesBufferDirty;
    _dynamicTransformMatrixDirtyFlags;
    /**
     * for effectiveKinematicState
     */
    _kinematicStatesBuffer;
    _isKinematicStatesBufferDirty;
    _commandBuffer;
    _count;
    constructor(count) {
        this.shouldSync = false;
        this._isDirty = false;
        this._motionStateMatricesBuffer = new Float32Array(count * 16);
        this._isMotionStateMatricesBufferDirty = false;
        this._motionStateMatrixDirtyFlags = new Uint8Array(count);
        this._dynamicTransformMatricesBuffer = null;
        this._isDynamicTransformMatricesBufferDirty = false;
        this._dynamicTransformMatrixDirtyFlags = null;
        this._kinematicStatesBuffer = null;
        this._isKinematicStatesBufferDirty = false;
        const commandBuffer = this._commandBuffer = new Array(count);
        for (let i = 0; i < count; ++i) {
            commandBuffer[i] = new Map();
        }
        this._count = count;
    }
    get needToCommit() {
        return this._isDirty;
    }
    commitToWasm(wasmInstance, bundlePtr, motionStatesPtr, kinematicStatesPtr, worldTransformPtrArray) {
        if (!this._isDirty) {
            return;
        }
        if (this._isMotionStateMatricesBufferDirty) {
            const n = this._motionStateMatricesBuffer;
            const m = motionStatesPtr.array;
            const motionStateMatrixDirtyFlags = this._motionStateMatrixDirtyFlags;
            const kinematicStates = kinematicStatesPtr.array;
            const count = this._count;
            let nOffset = 0;
            let mOffset = 0;
            for (let i = 0; i < count; ++i) {
                if (motionStateMatrixDirtyFlags[i] === 0) {
                    nOffset += 16;
                    mOffset += 20 /* Constants.MotionStateSizeInFloat32Array */;
                    continue;
                }
                m[mOffset + 4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 0] = n[nOffset];
                m[mOffset + 8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 0] = n[nOffset + 1];
                m[mOffset + 12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 0] = n[nOffset + 2];
                m[mOffset + 4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 1] = n[nOffset + 4];
                m[mOffset + 8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 1] = n[nOffset + 5];
                m[mOffset + 12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 1] = n[nOffset + 6];
                m[mOffset + 4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 2] = n[nOffset + 8];
                m[mOffset + 8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 2] = n[nOffset + 9];
                m[mOffset + 12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 2] = n[nOffset + 10];
                m[mOffset + 16 /* MotionStateOffsetsInFloat32Array.Translation */ + 0] = n[nOffset + 12];
                m[mOffset + 16 /* MotionStateOffsetsInFloat32Array.Translation */ + 1] = n[nOffset + 13];
                m[mOffset + 16 /* MotionStateOffsetsInFloat32Array.Translation */ + 2] = n[nOffset + 14];
                if ((kinematicStates[i] & 240 /* TemporalKinematicState.ReadMask */) !== 0 /* TemporalKinematicState.Disabled */) {
                    kinematicStates[i] = (kinematicStates[i] & 15 /* TemporalKinematicState.WriteMask */) | 32 /* TemporalKinematicState.WaitForChange */;
                }
                motionStateMatrixDirtyFlags[i] = 0;
                nOffset += 16;
                mOffset += 20 /* Constants.MotionStateSizeInFloat32Array */;
            }
            this._isMotionStateMatricesBufferDirty = false;
        }
        if (this._isDynamicTransformMatricesBufferDirty) {
            const n = this._dynamicTransformMatricesBuffer;
            const m = worldTransformPtrArray;
            const dynamicTransformMatrixDirtyFlags = this._dynamicTransformMatrixDirtyFlags;
            const count = this._count;
            let nOffset = 0;
            for (let i = 0; i < count; ++i) {
                if (dynamicTransformMatrixDirtyFlags[i] === 0) {
                    nOffset += 16;
                    continue;
                }
                const nthM = m[i].array;
                nthM[0 /* BtTransformOffsets.MatrixRowX */ + 0] = n[nOffset];
                nthM[16 /* BtTransformOffsets.MatrixRowY */ + 0] = n[nOffset + 1];
                nthM[32 /* BtTransformOffsets.MatrixRowZ */ + 0] = n[nOffset + 2];
                nthM[0 /* BtTransformOffsets.MatrixRowX */ + 1] = n[nOffset + 4];
                nthM[16 /* BtTransformOffsets.MatrixRowY */ + 1] = n[nOffset + 5];
                nthM[32 /* BtTransformOffsets.MatrixRowZ */ + 1] = n[nOffset + 6];
                nthM[0 /* BtTransformOffsets.MatrixRowX */ + 2] = n[nOffset + 8];
                nthM[16 /* BtTransformOffsets.MatrixRowY */ + 2] = n[nOffset + 9];
                nthM[32 /* BtTransformOffsets.MatrixRowZ */ + 2] = n[nOffset + 10];
                nthM[48 /* BtTransformOffsets.Translation */ + 0] = n[nOffset + 12];
                nthM[48 /* BtTransformOffsets.Translation */ + 1] = n[nOffset + 13];
                nthM[48 /* BtTransformOffsets.Translation */ + 2] = n[nOffset + 14];
                dynamicTransformMatrixDirtyFlags[i] = 0;
                nOffset += 16;
            }
            this._isDynamicTransformMatricesBufferDirty = false;
        }
        if (this._isKinematicStatesBufferDirty) {
            const kinematicStatesBuffer = this._kinematicStatesBuffer;
            const kinematicStates = kinematicStatesPtr.array;
            const count = this._count;
            for (let i = 0; i < count; ++i) {
                if (kinematicStatesBuffer[i] === 0) {
                    kinematicStates[i] = (kinematicStates[i] & 240 /* KinematicToggleState.WriteMask */) | 0 /* KinematicToggleState.Disabled */;
                }
                else {
                    kinematicStates[i] = (kinematicStates[i] & 240 /* KinematicToggleState.WriteMask */) | 1 /* KinematicToggleState.Enabled */;
                }
            }
            this._isKinematicStatesBufferDirty = false;
        }
        const commandBuffer = this._commandBuffer;
        for (let index = 0; index < this._count; ++index) {
            if (commandBuffer[index].size === 0) {
                continue;
            }
            for (const [command, args] of commandBuffer[index]) {
                switch (command) {
                    case 0 /* RigidBodyCommand.SetDamping */:
                        wasmInstance.rigidBodyBundleSetDamping(bundlePtr, index, args[0], args[1]);
                        break;
                    case 1 /* RigidBodyCommand.SetMassProps */: {
                        const localInertia = args[1];
                        wasmInstance.rigidBodyBundleSetMassProps(bundlePtr, index, args[0], localInertia.x, localInertia.y, localInertia.z);
                        break;
                    }
                    case 2 /* RigidBodyCommand.Translate */: {
                        const translation = args[0];
                        wasmInstance.rigidBodyBundleTranslate(bundlePtr, index, translation.x, translation.y, translation.z);
                        break;
                    }
                    case 3 /* RigidBodyCommand.SetLinearVelocity */: {
                        const linearVelocity = args[0];
                        wasmInstance.rigidBodyBundleSetLinearVelocity(bundlePtr, index, linearVelocity.x, linearVelocity.y, linearVelocity.z);
                        break;
                    }
                    case 4 /* RigidBodyCommand.SetAngularVelocity */: {
                        const angularVelocity = args[0];
                        wasmInstance.rigidBodyBundleSetAngularVelocity(bundlePtr, index, angularVelocity.x, angularVelocity.y, angularVelocity.z);
                        break;
                    }
                }
            }
            commandBuffer[index].clear();
        }
        this._isDirty = false;
    }
    setTransformMatrixFromArray(_motionStatesPtr, _kinematicStatesPtr, index, array, offset) {
        const m = this._motionStateMatricesBuffer;
        const motionStateMatrixDirtyFlags = this._motionStateMatrixDirtyFlags;
        const mOffset = index * 16;
        m[mOffset + 0] = array[offset];
        m[mOffset + 1] = array[offset + 1];
        m[mOffset + 2] = array[offset + 2];
        m[mOffset + 3] = 0;
        m[mOffset + 4] = array[offset + 4];
        m[mOffset + 5] = array[offset + 5];
        m[mOffset + 6] = array[offset + 6];
        m[mOffset + 7] = 0;
        m[mOffset + 8] = array[offset + 8];
        m[mOffset + 9] = array[offset + 9];
        m[mOffset + 10] = array[offset + 10];
        m[mOffset + 11] = 0;
        m[mOffset + 12] = array[offset + 12];
        m[mOffset + 13] = array[offset + 13];
        m[mOffset + 14] = array[offset + 14];
        m[mOffset + 15] = 1;
        motionStateMatrixDirtyFlags[index] = 1;
        this._isMotionStateMatricesBufferDirty = true;
        this._isDirty = true;
    }
    setTransformMatricesFromArray(_motionStatesPtr, _kinematicStatesPtr, array, offset) {
        this._motionStateMatricesBuffer.set(array, offset);
        this._motionStateMatrixDirtyFlags.fill(1);
        this._isMotionStateMatricesBufferDirty = true;
        this._isDirty = true;
    }
    setDynamicTransformMatrixFromArray(_worldTransformPtrArray, index, array, offset) {
        if (this._dynamicTransformMatricesBuffer === null) {
            this._dynamicTransformMatricesBuffer = new Float32Array(this._motionStateMatricesBuffer.length);
        }
        if (this._dynamicTransformMatrixDirtyFlags === null) {
            this._dynamicTransformMatrixDirtyFlags = new Uint8Array(this._motionStateMatrixDirtyFlags.length);
        }
        const m = this._dynamicTransformMatricesBuffer;
        const mOffset = index * 16;
        m[mOffset + 0] = array[offset];
        m[mOffset + 1] = array[offset + 1];
        m[mOffset + 2] = array[offset + 2];
        m[mOffset + 3] = 0;
        m[mOffset + 4] = array[offset + 4];
        m[mOffset + 5] = array[offset + 5];
        m[mOffset + 6] = array[offset + 6];
        m[mOffset + 7] = 0;
        m[mOffset + 8] = array[offset + 8];
        m[mOffset + 9] = array[offset + 9];
        m[mOffset + 10] = array[offset + 10];
        m[mOffset + 11] = 0;
        m[mOffset + 12] = array[offset + 12];
        m[mOffset + 13] = array[offset + 13];
        m[mOffset + 14] = array[offset + 14];
        m[mOffset + 15] = 1;
        this._dynamicTransformMatrixDirtyFlags[index] = 1;
        this._isDynamicTransformMatricesBufferDirty = true;
        this._isDirty = true;
    }
    getEffectiveKinematicState(_kinematicStatesPtr, index) {
        // we can't access _kinematicStatesPtr directly here
        // because it accessed from wasm side for read/write temporal kinematic state
        if (this._kinematicStatesBuffer === null) {
            return false;
        }
        return this._kinematicStatesBuffer[index] !== 0;
    }
    setEffectiveKinematicState(_kinematicStatesPtr, index, value) {
        if (this._kinematicStatesBuffer === null) {
            if (!value) {
                return; // no change needed
            }
            this._kinematicStatesBuffer = new Uint8Array(this._count);
        }
        const kinematicStatesBuffer = this._kinematicStatesBuffer;
        if (kinematicStatesBuffer[index] === +value) {
            return; // no change needed
        }
        kinematicStatesBuffer[index] = +value;
        this._isKinematicStatesBufferDirty = true;
        this._isDirty = true;
    }
    setDamping(_wasmInstance, _bundlePtr, index, linearDamping, angularDamping) {
        this._commandBuffer[index].set(0 /* RigidBodyCommand.SetDamping */, [linearDamping, angularDamping]);
        this._isDirty = true;
    }
    // this member is not updated by wasm so no need to synchronization before read
    getLinearDamping(wasmInstance, bundlePtr, index) {
        return wasmInstance.rigidBodyBundleGetLinearDamping(bundlePtr, index);
    }
    // this member is not updated by wasm so no need to synchronization before read
    getAngularDamping(wasmInstance, bundlePtr, index) {
        return wasmInstance.rigidBodyBundleGetAngularDamping(bundlePtr, index);
    }
    setMassProps(_wasmInstance, _bundlePtr, index, mass, localInertia) {
        this._commandBuffer[index].set(1 /* RigidBodyCommand.SetMassProps */, [mass, localInertia.clone()]);
        this._isDirty = true;
    }
    // this member is not updated by wasm so no need to synchronization before read
    getMass(wasmInstance, bundlePtr, index) {
        return wasmInstance.rigidBodyBundleGetMass(bundlePtr, index);
    }
    // this member is not updated by wasm so no need to synchronization before read
    getLocalInertia(wasmInstance, bundlePtr, index) {
        const outBufferPtr = wasmInstance.allocateBuffer(3 * 4 /* Constants.A32BytesPerElement */);
        const outBuffer = wasmInstance.createTypedArray(Float32Array, outBufferPtr, 3).array;
        wasmInstance.rigidBodyBundleGetLocalInertia(bundlePtr, index, outBufferPtr);
        const result = new Vector3(outBuffer[0], outBuffer[1], outBuffer[2]);
        wasmInstance.deallocateBuffer(outBufferPtr, 3 * 4 /* Constants.A32BytesPerElement */);
        return result;
    }
    translate(_wasmInstance, _bundlePtr, index, translation) {
        this._commandBuffer[index].set(2 /* RigidBodyCommand.Translate */, [translation.clone()]);
        this._isDirty = true;
    }
    setLinearVelocity(index, linearVelocity) {
        this._commandBuffer[index].set(3 /* RigidBodyCommand.SetLinearVelocity */, [linearVelocity.clone()]);
        this._isDirty = true;
    }
    setAngularVelocity(index, angularVelocity) {
        this._commandBuffer[index].set(4 /* RigidBodyCommand.SetAngularVelocity */, [angularVelocity.clone()]);
        this._isDirty = true;
    }
}
